import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaSitemap, FaTrash } from 'react-icons/fa';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import colors from '../constants/colors';

const getAuthToken = () => localStorage.getItem('authToken');

const AccountTypes = () => {
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
    parent_id: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAccountTypes();
  }, []);

  const fetchAccountTypes = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-account-types`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        setAccountTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching account types:', error);
      setError('Failed to load account types');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (accountType = null) => {
    if (accountType) {
      setEditMode(true);
      setFormData({
        id: accountType.id,
        name: accountType.name,
        description: accountType.description || '',
        parent_id: accountType.parent_id || '',
      });
    } else {
      setEditMode(false);
      setFormData({
        id: null,
        name: '',
        description: '',
        parent_id: '',
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      id: null,
      name: '',
      description: '',
      parent_id: '',
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Account type name is required');
      return;
    }

    if (!formData.parent_id) {
      setError('Parent account type is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = getAuthToken();
      const endpoint = editMode ? 'update-account-type' : 'create-account-type';
      const payload = editMode
        ? {
            id: formData.id,
            name: formData.name.trim(),
            description: formData.description.trim(),
            parent_id: parseInt(formData.parent_id),
          }
        : {
            name: formData.name.trim(),
            description: formData.description.trim(),
            parent_id: parseInt(formData.parent_id),
          };

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        await fetchAccountTypes();
        handleCloseModal();
      } else {
        setError(response.data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving account type:', error);
      setError(error.response?.data?.message || 'Failed to save account type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (accountType) => {
    // Check if it's a system type
    if (accountType.is_system === 1) {
      alert('System account types cannot be deleted.');
      return;
    }

    // Check if it has children
    const hasChildren = accountTypes.some(
      (type) => type.parent_id === accountType.id,
    );
    if (hasChildren) {
      alert(
        'This account type has sub-categories and cannot be deleted. Please delete the sub-categories first.',
      );
      return;
    }

    const confirmation = window.confirm(
      `Are you sure you want to delete "${accountType.name}"?\n\nThis action cannot be undone.`,
    );

    if (!confirmation) return;

    setSaving(true);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete-account-type`,
        { id: accountType.id },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        await fetchAccountTypes();
      } else {
        alert(response.data.message || 'Failed to delete account type');
      }
    } catch (error) {
      console.error('Error deleting account type:', error);
      alert(
        error.response?.data?.message ||
          'Failed to delete account type. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  // Build hierarchical structure
  const buildHierarchy = (types, parentId = null, level = 0) => {
    const result = [];
    types
      .filter((type) => type.parent_id === parentId)
      .forEach((type) => {
        result.push({ ...type, level });
        result.push(...buildHierarchy(types, type.id, level + 1));
      });
    return result;
  };

  const hierarchicalTypes = buildHierarchy(accountTypes);

  return (
    <Layout>
      <div className="container-fluid">
        {/* Compact Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '12px',
            backgroundColor: colors.sidebar.background,
            padding: '6px',
            borderRadius: '6px',
            color: colors.sidebar.text,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: '1rem',
                color: 'white',
              }}
            >
              <FaSitemap />
              Account Types
            </span>
          </div>
          <Button
            onClick={() => handleShowModal()}
            variant="light"
            size="sm"
            style={{
              width: '120px',
              height: '28px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
              fontSize: '0.85rem',
              fontWeight: '600',
            }}
            title="Add Account Type"
          >
            <FaPlus className="me-2" />
            New Type
          </Button>
        </div>

        {/* Error Alert */}
        {error && !showModal && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {/* Account Types Table */}
        <Card
          style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading account types...</p>
              </div>
            ) : hierarchicalTypes.length === 0 ? (
              <div className="text-center py-5">
                <FaSitemap
                  style={{
                    fontSize: '3rem',
                    color: '#dee2e6',
                    marginBottom: '1rem',
                  }}
                />
                <p className="text-muted">No account types found</p>
                <Button variant="primary" onClick={() => handleShowModal()}>
                  Create First Account Type
                </Button>
              </div>
            ) : (
              <Table responsive hover className="mb-0">
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                      }}
                    >
                      Name
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                      }}
                    >
                      Description
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                        width: '100px',
                        textAlign: 'center',
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                        width: '100px',
                        textAlign: 'center',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hierarchicalTypes.map((type, idx) => (
                    <tr
                      key={type.id}
                      style={{
                        background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          idx % 2 === 0 ? '#fff' : '#fafbfc';
                      }}
                    >
                      <td
                        style={{
                          padding: '14px 20px',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <div
                          style={{
                            paddingLeft: `${type.level * 24}px`,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {type.level > 0 && (
                            <span
                              style={{ color: '#adb5bd', marginRight: '8px' }}
                            >
                              └─
                            </span>
                          )}
                          <span
                            style={{
                              fontWeight: type.level === 0 ? '600' : 'normal',
                              color: '#2c3e50',
                            }}
                          >
                            {type.name}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#6c757d',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {type.description || '-'}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {type.is_system === 1 ? (
                          <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                            System
                          </Badge>
                        ) : (
                          <Badge bg="primary" style={{ fontSize: '0.75rem' }}>
                            Custom
                          </Badge>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {type.is_system === 0 && (
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              justifyContent: 'center',
                            }}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowModal(type)}
                              disabled={saving}
                              style={{
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '0.85rem',
                              }}
                              title="Edit"
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(type)}
                              disabled={saving}
                              style={{
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '0.85rem',
                              }}
                              title="Delete"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Create/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderBottom: 'none',
            }}
          >
            <Modal.Title>
              {editMode ? 'Edit Account Type' : 'New Account Type'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body style={{ padding: '24px' }}>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Name <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter account type name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  style={{
                    borderRadius: '8px',
                    padding: '10px 14px',
                    border: '1px solid #dee2e6',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow =
                      '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dee2e6';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Parent Account Type{' '}
                  <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Select
                  value={formData.parent_id}
                  onChange={(e) =>
                    setFormData({ ...formData, parent_id: e.target.value })
                  }
                  required
                  style={{
                    borderRadius: '8px',
                    padding: '10px 14px',
                    border: '1px solid #dee2e6',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow =
                      '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dee2e6';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">Select parent account type</option>
                  {accountTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Description
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter description (optional)"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  style={{
                    borderRadius: '8px',
                    padding: '10px 14px',
                    border: '1px solid #dee2e6',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow =
                      '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dee2e6';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer
              style={{ borderTop: '1px solid #e9ecef', padding: '16px 24px' }}
            >
              <Button
                variant="light"
                onClick={handleCloseModal}
                disabled={saving}
                style={{
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontWeight: '600',
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                style={{
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontWeight: '600',
                }}
              >
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    {editMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : editMode ? (
                  'Update Account Type'
                ) : (
                  'Create Account Type'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default AccountTypes;
